import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  getProducts,
  searchProducts,
  getAllProducts,
} from "../../../../services/pos-api";
import { getBrandsList } from "../../../../services/brands-api";
import { getCategoriesList } from "../../../../services/category-api";
import { handleFormattedValidationErrors } from "../../../../utils/errorHandlers";
import FilterIcon from "../../../../assets/filter-icon.svg";
import MinusIcon from "../../../../assets/minus-icon.svg";
import PlusIcon from "../../../../assets/plus-icon.svg";
import MenuIcon from "../../../../assets/icons/MenuIcon.js";
import OutOfStockModal from "./OutOfStockModal";
import { useAppSelector } from "../../../../store/hooks";

const ProductSection = ({
  selectedCategory,
  selectedSubCategory,
  onBackToCategories,
  onBackToSubCategories,
  cartItems = [],
  onUpdateCartQuantity,
  onAddItemToCart,
  searchResults = null,
  searchQuery = "",
  isSearchMode = false,
  isCartVisible = false,
  onFiltersChange,
  refreshTrigger = 0,
}) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isDeviceDropdownOpen, setIsDeviceDropdownOpen] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState("All Device");
  const [isBrandDropdownOpen, setIsBrandDropdownOpen] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState("All Brands");
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false);
  const [selectedCategoryFilter, setSelectedCategoryFilter] =
    useState("All Categories");
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isOutOfStockModalOpen, setIsOutOfStockModalOpen] = useState(false);
  const [productQuantities, setProductQuantities] = useState({});
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isBrandsLoading, setIsBrandsLoading] = useState(false);
  const [isCategoriesLoading, setIsCategoriesLoading] = useState(false);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [, setIsTagDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const brandDropdownRef = useRef(null);
  const categoryDropdownRef = useRef(null);
  const tagDropdownRef = useRef(null);
  const isQuantityChanging = useRef(false);
  const isLoadingRef = useRef(false);
  const prevFiltersRef = useRef(null);
  const { isPartsURL, isAccessoriesURL } = useAppSelector((state) => state.domain);
  const deviceOptions = ["All Device", "New Device", "Used Device"];

  // Define functions before useEffect to avoid hoisting issues
  const handleSearchResults = useCallback(() => {
    try {
      setLoading(true);
      setError(null);

      const transformedProducts = transformProductData(searchResults);
      setProducts(transformedProducts);
      setCurrentPage(1);
      setHasMore(false);
    } catch (error) {
      setError("Failed to process search results");
    } finally {
      setLoading(false);
    }
  }, [searchResults]);

  const fetchAllProducts = useCallback(async (deviceType = null, brandId = null, categoryId = null, forceRefresh = false, page = 1) => {
    if (isLoadingRef.current) return;

    const filterKey = JSON.stringify({ deviceType, brandId, categoryId });
    const prevFilterKey = prevFiltersRef.current ? JSON.stringify(prevFiltersRef.current) : '';

    if (!forceRefresh && prevFilterKey === filterKey && prevFiltersRef.current !== null && page === 1) {
      return;
    }

    try {
      isLoadingRef.current = true;
      if (page === 1) {
        prevFiltersRef.current = { deviceType, brandId, categoryId };
        setLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);

      let device = '';
      if (deviceType === "New Device") device = 'new';
      else if (deviceType === "Used Device") device = 'used';

      const response = await getAllProducts("", brandId, categoryId, device, page);
      const fetchedProducts = response.results || [];
      const transformedProducts = transformProductData(fetchedProducts);

      if (page === 1) {
        setProducts(transformedProducts);
      } else {
        setProducts(prev => [...prev, ...transformedProducts]);
      }

      setHasMore(!!response.next);
      setCurrentPage(page);
    } catch (error) {
      setError("Failed to load products");
      handleFormattedValidationErrors(error, "Failed to load products");
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
      isLoadingRef.current = false;
    }
  }, []);

  const fetchProducts = useCallback(async (deviceType = null, forceRefresh = false, page = 1) => {
    if (isLoadingRef.current) return;
    if (!selectedCategory?.id || !selectedSubCategory?.id) return;

    const filterKey = JSON.stringify({
      categoryId: selectedCategory.id,
      subCategoryId: selectedSubCategory.id,
      deviceType
    });
    const prevFilterKey = prevFiltersRef.current ? JSON.stringify(prevFiltersRef.current) : '';

    if (!forceRefresh && prevFilterKey === filterKey && prevFiltersRef.current !== null && page === 1) {
      return;
    }

    try {
      isLoadingRef.current = true;
      if (page === 1) {
        prevFiltersRef.current = {
          categoryId: selectedCategory.id,
          subCategoryId: selectedSubCategory.id,
          deviceType
        };
        setLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);

      const response = await getProducts(
        selectedCategory.id,
        selectedSubCategory.id,
        "",
        deviceType,
        page
      );
      const fetchedProducts = response.results || [];
      const transformedProducts = transformProductData(fetchedProducts);

      if (page === 1) {
        setProducts(transformedProducts);
      } else {
        setProducts(prev => [...prev, ...transformedProducts]);
      }

      setHasMore(!!response.next);
      setCurrentPage(page);
    } catch (error) {
      setError("Failed to load products");
      handleFormattedValidationErrors(error, "Failed to load products");
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
      isLoadingRef.current = false;
    }
  }, [selectedCategory?.id, selectedSubCategory?.id]);

  // Refresh products when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      if (isSearchMode) {
        fetchSearchResultsWithFilter(selectedDevice, selectedBrandId, selectedCategoryId);
      } else if (selectedCategory && selectedCategory.name === "All Products") {
        fetchAllProducts(selectedDevice, selectedBrandId, selectedCategoryId, true);
      } else if (selectedCategory && selectedSubCategory) {
        fetchProducts(selectedDevice, true);
      }
    }
  }, [refreshTrigger]);

  // Fetch products when component mounts or dependencies change
  useEffect(() => {
    // Don't fetch if we're in the middle of a quantity change
    if (isQuantityChanging.current) {
      return;
    }

    if (isSearchMode && searchResults) {
      // Handle search results
      handleSearchResults();
    } else if (
      selectedCategory &&
      selectedSubCategory &&
      selectedCategory.id &&
      selectedSubCategory.id
    ) {
      // Fetch products with category and subcategory filters
      fetchProducts();
    } else if (selectedCategory && selectedCategory.name === "All Products") {
      // Fetch all products without category/subcategory filters
      fetchAllProducts();
    }
  }, [selectedCategory, selectedSubCategory, searchResults, isSearchMode, handleSearchResults, fetchProducts, fetchAllProducts]);

  // Update filtered products when products change (no local filtering needed)
  useEffect(() => {
    setFilteredProducts(products);
  }, [products]);

  // Sync local productQuantities with cart items
  useEffect(() => {
    const newQuantities = {};
    cartItems.forEach((item) => {
      const productKey = `${item.id}-${item.condition}`;
      newQuantities[productKey] = item.quantity;
    });
    setProductQuantities(newQuantities);
  }, [cartItems]);

  const fetchBrandsList = async (forceRefresh = false) => {
    try {
      setIsBrandsLoading(true);
      const response = await getBrandsList({ page_size: 100 });
      setBrands(response.results || []);
    } catch (error) {
      // Handle error silently
    } finally {
      setIsBrandsLoading(false);
    }
  };

  const fetchCategoriesList = async (forceRefresh = false) => {
    try {
      setIsCategoriesLoading(true);
      const response = await getCategoriesList({ page_size: 100 });
      setCategories(response.results || []);
    } catch (error) {
      // Handle error silently
    } finally {
      setIsCategoriesLoading(false);
    }
  };

  const transformProductData = (fetchedProducts) => {
    return fetchedProducts.map((product) => {
      // Format product details like in ProductTable
      const formatProductDetails = (product) => {
        const details = [];

        if (product.ram) details.push(product.ram);
        if (product.storage) details.push(product.storage);
        if (product.suffix) details.push(product.suffix);

        // Extract color from variants
        if (product.variants && product.variants.length > 0) {
          const variantDetails = product.variants
            .map((variant) => Object.values(variant).filter(Boolean).join(", "))
            .filter(Boolean)
            .join(", ");
          if (variantDetails) {
            details.push(variantDetails);
          }
        }

        if (product.used_mobile !== undefined && product.used_mobile !== null) {
          details.push(product.used_mobile ? "Used" : "New");
        }

        return details.join(" | ");
      };

      return {
        id: product.id,
        name: product.name,
        details: formatProductDetails(product),
        price: parseFloat(
          product.avg_selling_price ||
          product.selling_price ||
          product.price ||
          0
        ),
        condition: product.used_mobile ? "Used" : "New",
        stock: product.in_stock || 0,
        image: product.image || null,
        brand: product.brand_name,
        category: product.category,
        ram: product.ram,
        storage: product.storage,
        suffix: product.suffix,
        description: product.description,
        avg_selling_price: parseFloat(
          product.avg_selling_price ||
          product.selling_price ||
          product.price ||
          0
        ) || 0,
        used_mobile: product.used_mobile,
        unique_id: product.unique_id || null, // Include unique_id if available
      };
    });
  };

  const fetchProductsWithFilter = async (device, brandId = null, categoryId = null, page = 1) => {
    if (isLoadingRef.current) return;

    try {
      isLoadingRef.current = true;
      if (page === 1) {
        prevFiltersRef.current = { device, brandId, categoryId };
        setLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);

      let deviceParam = '';
      if (device === "New Device") deviceParam = 'new';
      else if (device === "Used Device") deviceParam = 'used';

      let response;
      if (selectedCategory && selectedCategory.name === "All Products") {
        response = await getAllProducts("", brandId, categoryId, deviceParam, page);
      } else {
        response = await getProducts(
          selectedCategory.id,
          selectedSubCategory.id,
          "",
          deviceParam,
          page
        );
      }
      const fetchedProducts = response.results || [];
      const transformedProducts = transformProductData(fetchedProducts);

      if (page === 1) {
        setProducts(transformedProducts);
      } else {
        setProducts(prev => [...prev, ...transformedProducts]);
      }

      setHasMore(!!response.next);
      setCurrentPage(page);
    } catch (error) {
      setError("Failed to load products");
      handleFormattedValidationErrors(error, "Failed to load products");
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
      isLoadingRef.current = false;
    }
  };

  const fetchSearchResultsWithFilter = async (device, brandId = null, categoryId = null, page = 1) => {
    if (isLoadingRef.current) return;

    try {
      isLoadingRef.current = true;
      if (page === 1) {
        setLoading(true);
      } else {
        setIsLoadingMore(true);
      }
      setError(null);

      let deviceParam = '';
      if (device === "New Device") deviceParam = 'new';
      else if (device === "Used Device") deviceParam = 'used';

      const response = await searchProducts(
        searchQuery,
        categoryId,
        brandId,
        deviceParam,
        page
      );
      const fetchedProducts = response.results || [];
      const transformedProducts = transformProductData(fetchedProducts);

      if (page === 1) {
        setProducts(transformedProducts);
      } else {
        setProducts(prev => [...prev, ...transformedProducts]);
      }

      setHasMore(!!response.next);
      setCurrentPage(page);
    } catch (error) {
      setError("Failed to load search results");
      handleFormattedValidationErrors(error, "Failed to load search results");
    } finally {
      setLoading(false);
      setIsLoadingMore(false);
      isLoadingRef.current = false;
    }
  };

  // Remove local filtering since we now use API filtering

  const handleDeviceSelect = async (device) => {
    setSelectedDevice(device);
    setIsDeviceDropdownOpen(false);

    if (onFiltersChange) {
      onFiltersChange({
        device,
        brandId: selectedBrandId,
        categoryId: selectedCategoryId,
      });
    }

    if (isSearchMode) {
      await fetchSearchResultsWithFilter(device, selectedBrandId, selectedCategoryId);
    } else if (selectedCategory && selectedCategory.name === "All Products") {
      await fetchAllProducts(device, selectedBrandId, selectedCategoryId);
    } else if (selectedCategory && selectedSubCategory) {
      await fetchProductsWithFilter(device, selectedBrandId, selectedCategoryId);
    }
  };

  const handleBrandSelect = (brand) => {
    setSelectedBrand(brand);
    setIsBrandDropdownOpen(false);

    const brandObj = brands.find(b => b.name === brand);
    const brandId = brand === "All Brands" ? null : (brandObj ? brandObj.id : null);
    setSelectedBrandId(brandId);

    if (onFiltersChange) {
      onFiltersChange({
        device: selectedDevice,
        brandId,
        categoryId: selectedCategoryId,
      });
    }

    if (isSearchMode) {
      fetchSearchResultsWithFilter(selectedDevice, brandId, selectedCategoryId);
    } else if (selectedCategory && selectedCategory.name === "All Products") {
      fetchAllProducts(selectedDevice, brandId, selectedCategoryId);
    } else if (selectedCategory && selectedSubCategory) {
      fetchProductsWithFilter(selectedDevice, brandId, selectedCategoryId);
    }
  };

  const handleCategorySelect = (category) => {
    setSelectedCategoryFilter(category);
    setIsCategoryDropdownOpen(false);

    const categoryObj = categories.find(c => c.name === category);
    const categoryId = category === "All Categories" ? null : (categoryObj ? categoryObj.id : null);
    setSelectedCategoryId(categoryId);

    if (onFiltersChange) {
      onFiltersChange({
        device: selectedDevice,
        brandId: selectedBrandId,
        categoryId,
      });
    }

    if (isSearchMode) {
      fetchSearchResultsWithFilter(selectedDevice, selectedBrandId, categoryId);
    } else if (selectedCategory && selectedCategory.name === "All Products") {
      fetchAllProducts(selectedDevice, selectedBrandId, categoryId);
    } else if (selectedCategory && selectedSubCategory) {
      fetchProductsWithFilter(selectedDevice, selectedBrandId, categoryId);
    }
  };

  const getProductImage = (product) => {
    // Use only API image, return null if not available
    return product.image && product.image.trim() !== "" ? product.image : null;
  };

  const handleProductClick = (product) => {
    if (product.stock === 0) {
      setIsOutOfStockModalOpen(true);
    } else {
      // Add product to cart when clicking on product card
      const currentQuantity = getProductQuantity(product);
      const newQuantity = currentQuantity + 1;

      if (newQuantity <= product.stock) {
        handleQuantityChange(product, newQuantity);
      }
    }
  };

  const handleQuantityChange = (product, newQuantity) => {
    if (newQuantity < 0) return;
    if (newQuantity > product.stock) return;

    // Set flag to prevent loading during quantity changes
    isQuantityChanging.current = true;

    const productKey = `${product.id}-${product.condition}`;
    setProductQuantities((prev) => ({
      ...prev,
      [productKey]: newQuantity,
    }));

    if (onUpdateCartQuantity) {
      // Check if item exists in cart (match by id and condition only, ignore unique_id)
      const existingCartItem = cartItems.find(
        (item) => item.id === product.id && item.condition === product.condition
      );

      if (existingCartItem) {
        // Update existing item
        onUpdateCartQuantity(
          product.id,
          product.condition,
          newQuantity,
          product.unique_id || null
        );
      } else if (newQuantity > 0) {
        // Add new item to cart
        if (onAddItemToCart) {
          onAddItemToCart({
            id: product.id,
            name: product.name,
            price: product.price,
            condition: product.condition,
            stock: product.stock,
            image: product.image,
            quantity: newQuantity,
            details: product.details,
            unique_id: product.unique_id || null, // Include unique_id if available
          });
        }
      }
    }

    // Reset flag after a short delay to allow for any async operations
    setTimeout(() => {
      isQuantityChanging.current = false;
    }, 100);
  };

  const getProductQuantity = (product) => {
    const productKey = `${product.id}-${product.condition}`;
    return productQuantities[productKey] || 0;
  };

  const closeOutOfStockModal = () => {
    setIsOutOfStockModalOpen(false);
  };

  // Infinity scroll
  useEffect(() => {
    const handleScroll = () => {
      if (isLoadingMore || !hasMore || loading) return;

      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const scrollHeight = document.documentElement.scrollHeight;
      const clientHeight = document.documentElement.clientHeight;

      if (scrollTop + clientHeight >= scrollHeight - 200) {
        const nextPage = currentPage + 1;

        if (isSearchMode) {
          fetchSearchResultsWithFilter(selectedDevice, selectedBrandId, selectedCategoryId, nextPage);
        } else if (selectedCategory && selectedCategory.name === "All Products") {
          fetchAllProducts(selectedDevice, selectedBrandId, selectedCategoryId, false, nextPage);
        } else if (selectedCategory && selectedSubCategory) {
          fetchProducts(selectedDevice, false, nextPage);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isLoadingMore, hasMore, loading, currentPage, isSearchMode, selectedDevice, selectedBrandId, selectedCategoryId, selectedCategory, selectedSubCategory, fetchSearchResultsWithFilter, fetchAllProducts, fetchProducts]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDeviceDropdownOpen(false);
      }
      if (
        brandDropdownRef.current &&
        !brandDropdownRef.current.contains(event.target)
      ) {
        setIsBrandDropdownOpen(false);
      }
      if (
        categoryDropdownRef.current &&
        !categoryDropdownRef.current.contains(event.target)
      ) {
        setIsCategoryDropdownOpen(false);
      }
      if (
        tagDropdownRef.current &&
        !tagDropdownRef.current.contains(event.target)
      ) {
        setIsTagDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  if (loading) {
    return (
      <div className="products-section">
        <div className="products-grid">
          {[...Array(8)].map((_, index) => (
            <div key={index} className="pos-product-card loading">
              <div className="pos-product-header">
                <span className="condition-tag skeleton"></span>
                <span className="stock-indicator skeleton"></span>
              </div>
              <div className="pos-product-image skeleton"></div>
              <div className="product-name skeleton"></div>
              <div className="product-footer">
                <div className="product-price skeleton"></div>
                <div className="quantity-controls skeleton"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="products-section">
        <div className="error-message">
          <p>{error}</p>
          <button onClick={fetchProducts} className="retry-btn">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`products-section ${isCartVisible ? "with-cart" : ""}`}>
      <div className="breadcrumb-container">
        <div className={`filter-buttons ${isCartVisible ? "with-cart" : ""}`}
        >
          {/* Left side - Brand and Category Dropdowns */}
          <div className="filter-left-group">
            {/* Brand Dropdown */}
            <div className="filter-dropdown-container" ref={brandDropdownRef}>
              <button
                className={`filter-btn dropdown-btn ${isBrandDropdownOpen ? "open" : ""
                  }`}
                onClick={() => {
                  if (!isBrandDropdownOpen && brands.length === 0) {
                    fetchBrandsList();
                  }
                  setIsBrandDropdownOpen(!isBrandDropdownOpen);
                }}
              >
                <MenuIcon />
                <span>{selectedBrand}</span>
              </button>
              {isBrandDropdownOpen && (
                <div className="filter-dropdown" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                  <div
                    className={`dropdown-option ${selectedBrand === "All Brands" ? "selected" : ""
                      }`}
                    onClick={() => handleBrandSelect("All Brands")}
                  >
                    {selectedBrand === "All Brands" && (
                      <span className="checkmark">✓</span>
                    )}
                    <span>All Brands</span>
                  </div>
                  {isBrandsLoading ? (
                    [...Array(3)].map((_, index) => (
                      <div key={index} className="dropdown-option skeleton">
                        <div className="skeleton-text"></div>
                      </div>
                    ))
                  ) : (
                    brands.map((brand, index) => (
                      <div
                        key={index}
                        className={`dropdown-option ${selectedBrand === brand.name ? "selected" : ""
                          }`}
                        onClick={() => handleBrandSelect(brand.name)}
                      >
                        {selectedBrand === brand.name && (
                          <span className="checkmark">✓</span>
                        )}
                        <span>{brand.name}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>

            {/* Category Dropdown */}
            <div
              className="filter-dropdown-container"
              ref={categoryDropdownRef}
            >
              <button
                className={`filter-btn dropdown-btn ${isCategoryDropdownOpen ? "open" : ""
                  }`}
                onClick={() => {
                  if (!isCategoryDropdownOpen && categories.length === 0) {
                    fetchCategoriesList();
                  }
                  setIsCategoryDropdownOpen(!isCategoryDropdownOpen);
                }}
              >
                <MenuIcon />
                <span>{selectedCategoryFilter}</span>
              </button>
              {isCategoryDropdownOpen && (
                <div className="filter-dropdown" style={{ maxHeight: '220px', overflowY: 'auto' }}>
                  <div
                    className={`dropdown-option ${selectedCategoryFilter === "All Categories"
                      ? "selected"
                      : ""
                      }`}
                    onClick={() => handleCategorySelect("All Categories")}
                  >
                    {selectedCategoryFilter === "All Categories" && (
                      <span className="checkmark">✓</span>
                    )}
                    <span>All Categories</span>
                  </div>
                  {isCategoriesLoading ? (
                    [...Array(3)].map((_, index) => (
                      <div key={index} className="dropdown-option skeleton">
                        <div className="skeleton-text"></div>
                      </div>
                    ))
                  ) : (
                    categories.map((category, index) => (
                      <div
                        key={index}
                        className={`dropdown-option ${selectedCategoryFilter === category.name
                          ? "selected"
                          : ""
                          }`}
                        onClick={() => handleCategorySelect(category.name)}
                      >
                        {selectedCategoryFilter === category.name && (
                          <span className="checkmark">✓</span>
                        )}
                        <span>{category.name}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right side - All Devices Dropdown */}
          {isAccessoriesURL ? ("") : (
            <div className={`filter-right-group ${isCartVisible ? "with-cart" : ""}`}>
              <div
                className="filter-dropdown-container"
                ref={dropdownRef}
              // style={{ width: "100%" }}
              >
                <button
                  className={`filter-btn dropdown-btn ${isDeviceDropdownOpen ? "open" : ""
                    }`}
                  // style={{ width: "100%" }}
                  onClick={() => setIsDeviceDropdownOpen(!isDeviceDropdownOpen)}
                >
                  <img src={FilterIcon} alt="Filter" />
                  <span>{selectedDevice}</span>
                </button>
                {isDeviceDropdownOpen && (
                  <div className="filter-dropdown no-scrollbar">
                    {deviceOptions.map((device, index) => (
                      <div
                        key={index}
                        className={`dropdown-option ${selectedDevice === device ? "selected" : ""
                          }`}
                        onClick={() => handleDeviceSelect(device)}
                      >
                        {selectedDevice === device && (
                          <span className="checkmark">✓</span>
                        )}
                        <span>{device}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

            </div>
          )}
        </div>
      </div>
      <div className={`products-grid ${isCartVisible ? "with-cart" : ""}`}>
        {filteredProducts.length > 0 ? (
          filteredProducts.map((product) => {
            const isOutOfStock = product.stock === 0;
            return (
              <div
                key={product.id}
                className={`pos-product-card ${isOutOfStock ? "out-of-stock-card" : ""
                  }`}
                onClick={() => handleProductClick(product)}
              >
                <div className="pos-product-header">
                  <span
                    className={`${isPartsURL ? "condition-tag-parts" : isAccessoriesURL ? "condition-tag-acc" : "condition-tag"} ${product.condition.toLowerCase()}`}
                  >
                    {product.condition}
                  </span>
                  <span
                    className={`stock-indicator ${product.stock - getProductQuantity(product) > 20
                      ? "in-stock"
                      : product.stock - getProductQuantity(product) < 20 &&
                        product.stock - getProductQuantity(product) > 0
                        ? "near-out-of-stock"
                        : "out-of-stock"
                      }`}
                  >
                    <span className="stock-indicator-icon" />
                    {product.stock - getProductQuantity(product)}
                  </span>
                </div>

                <div className="pos-product-image">
                  {getProductImage(product) ? (
                    <img
                      src={getProductImage(product)}
                      alt={product.name}
                      style={{ width: "106px", height: "126px" }}
                      className={isOutOfStock ? "out-of-stock-image" : ""}
                    />
                  ) : (
                    <div className="item-image-placeholder">
                      <span className="placeholder-text">{product.name}</span>
                    </div>
                  )}
                </div>
                <div className="product-name">
                  <div
                    className="pos-product-name-text"
                    style={{ fontWeight: "bold" }}
                    title={product.name}
                  >
                    {product.name}
                  </div>
                  {product.details && (
                    <div
                      className="pos-product-name-details"
                      title={product.details}
                      style={{
                        fontSize: "0.8em",
                        color: "#666",
                        marginTop: "4px",
                        fontWeight: "bold",
                      }}
                    >
                      {product.details}
                    </div>
                  )}
                </div>
                <div className="product-footer">
                  <div className="product-price">
                    €{(Number(product.avg_selling_price) || 0).toFixed(2)}
                  </div>
                  <div
                    className={`quantity-controls ${isOutOfStock ? "disabled" : ""
                      }`}
                  >
                    <button
                      className={`${isPartsURL ? "quantity-btn-parts" : isAccessoriesURL ? "quantity-btn-acc" : "quantity-btn"} minus`}
                      disabled={
                        isOutOfStock || getProductQuantity(product) <= 0
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQuantityChange(
                          product,
                          getProductQuantity(product) - 1
                        );
                      }}
                    >
                      <img src={MinusIcon} alt="Minus" />
                    </button>
                    <span className="quantity-display">
                      {getProductQuantity(product)}
                    </span>
                    <button
                      className={`${isPartsURL ? "quantity-btn-parts" : isAccessoriesURL ? "quantity-btn-acc" : "quantity-btn"} plus`}
                      disabled={
                        isOutOfStock ||
                        getProductQuantity(product) >= product.stock
                      }
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQuantityChange(
                          product,
                          getProductQuantity(product) + 1
                        );
                      }}
                    >
                      <img src={PlusIcon} alt="Plus" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <div className="no-products-found">
            <div className="no-products-message">
              <h3>No Products Found</h3>
              <p>No products available for the selected device filter.</p>
            </div>
          </div>
        )}
      </div>

      {isLoadingMore && (
        <div className="loading-more" style={{ textAlign: 'center', padding: '20px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      <OutOfStockModal
        isOpen={isOutOfStockModalOpen}
        onClose={closeOutOfStockModal}
      />
    </div>
  );
};

export default ProductSection;